/**
 * LocationSearch Component
 * Allows users to search and select any location worldwide
 * Uses frontend-based search for instant results
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Search, MapPin, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { LOCATIONS, LocationData, searchLocations, getDisplayName } from '../../data/locations';

// Re-export LocationData for backward compatibility
export type { LocationData } from '../../data/locations';

// For backward compatibility, also export WORLD_CITIES
export const WORLD_CITIES: LocationData[] = LOCATIONS;

interface LocationSearchProps {
  onLocationSelect: (location: LocationData | null) => void;
  initialLocation?: LocationData | null;
  placeholder?: string;
}

export const LocationSearch: React.FC<LocationSearchProps> = ({
  onLocationSelect,
  initialLocation,
  placeholder = 'Search for a city...',
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(initialLocation || null);
  const searchRef = useRef<HTMLDivElement>(null);

  // Filter cities based on search query using the shared search function
  const filteredCities = useMemo(() => {
    return searchLocations(searchQuery, 10);
  }, [searchQuery]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (selectedLocation) {
      setSelectedLocation(null);
      onLocationSelect(null);
    }
    setIsOpen(value.length > 0);
  };

  const handleSelectLocation = (location: LocationData) => {
    setSelectedLocation(location);
    setSearchQuery(getDisplayName(location));
    setIsOpen(false);
    onLocationSelect(location);
  };

  const handleClear = () => {
    setSearchQuery('');
    setSelectedLocation(null);
    setIsOpen(false);
    onLocationSelect(null);
  };

  const displayValue = selectedLocation
    ? getDisplayName(selectedLocation)
    : searchQuery;

  return (
    <div className="flex flex-col gap-2 relative" ref={searchRef}>
      <Label htmlFor="location-search">Location</Label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          id="location-search"
          type="text"
          autoComplete="off"
          placeholder={placeholder}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(searchQuery.length > 0)}
          className={cn(
            "pl-10 pr-10",
            selectedLocation && "pr-10"
          )}
        />
        {selectedLocation && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear location"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Dropdown Results */}
      {isOpen && filteredCities.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white dark:bg-zinc-900 border border-border rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredCities.map((city, index) => (
            <button
              key={`${city.id}-${index}`}
              type="button"
              onClick={() => handleSelectLocation(city)}
              className="w-full text-left px-4 py-2 hover:bg-accent hover:text-accent-foreground flex items-center gap-2 transition-colors"
            >
              <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{city.name}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {city.state ? `${city.state}, ${city.country}` : city.country}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {isOpen && searchQuery.length > 0 && filteredCities.length === 0 && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white dark:bg-zinc-900 border border-border rounded-md shadow-lg p-4 text-sm text-muted-foreground text-center">
          No locations found. Try a different search term.
        </div>
      )}
    </div>
  );
};

export default LocationSearch;
